import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import MissionService from '../../../service/MissionService';
import { getEnvVar } from '../../../envUtils';
import { showToastMessage } from '../../../utils/commonFunctions';
import {
  applyOrbitReportUpdatesToTreeItems,
  mapApiSatToTreeItem,
  mapOrbitReportToSatelliteUpdates,
} from './lib/missionSimulationMapper';
import { mapAccessAnalysisReportsResponse } from './lib/accessAnalysisReports';
import { useMDCockpit } from './hooks/useMDCockpit';
import { useCesiumMap } from './hooks/useCesiumMap';
import TopBar from './modules/TopBar';
import LeftSideBar from './modules/LeftSideBar';
import Timeline from './modules/Timeline';
import { StatusBar } from './modules/BottomBar';
import { ReportDrawer } from './modules/ReportSection';
import { ModelConfigModal, PropertyPanel, LayersPanel } from './modules/SettingLayer';
import { AIPanel, AnalyserPanel, CoverageAnalysisPanel, SensorConfigPanel, AccessAnalysisReportsPanel, RightTools } from './modules/AnalysisBar';
import { FigmaCustomSelect } from './modules/AnalysisBar/AccessAnalysis/components/AccessAnalysisReportsPanel';
import { CesiumMap, MapVignette, CommandBar, LoadingScreen } from './modules/Core';
import { AddSatModal, AddGroundStationModal, GeoFeaturePanel } from './modules/LeftSideBar';
import { isPropagationConfigured } from './lib/modelDefaults';
import type { OrbitPointStatus, OrbitStatusMeta, SatGroup, Satellite } from './types/mission';
import styles from './MDCockpit.module.scss';

interface Props {
  cesium: ReturnType<typeof useCesiumMap>;
}

type AccessWindowStatus = 'running' | 'progress' | 'completed' | 'failed';
type AccessAnalysisStatus = 'analyzing' | 'calculating_access_windows' | 'completed' | 'failed';
type OrbitUpdateLock = {
  label: string;
  detail?: string;
  mode?: 'default' | 'initialOrbitLoad';
};

interface AccessWindowRunState {
  status: AccessWindowStatus;
  runId?: string;
  pairsTotal?: number;
  pairsDone?: number;
  pairsFailed?: number;
  error?: string;
}

const MDCockpitInner: React.FC<Props> = ({ cesium }) => {
  const {
    satellites,
    modelData,
    fetchGroundStations,
    fetchGeoFeatures,
    openModelConfig,
    setModelData,
    setSatellites,
    updateSatellites,
    applyOrbitEvent,
    setAccessWindowPairs,
    accessAnalysisReports,
    setAccessAnalysisReports,
    upsertAccessAnalysisReport,
    coverageAnalysisReports,
    setCoverageAnalysisReports,
    upsertCoverageAnalysisReport,
    selectedCoverageReportId,
    selectedCoverageReportDetail,
    selectedReportDetail,
    coverageSectionTab,
    heatmapMetricMode,
    setHeatmapMetricMode,
    timelineCollapsed,
    timelineHeight,
    revisitThresholds,
    setRevisitThresholds,
    shouldBlockUI,
    setShouldBlockUI,
    leftPanelCollapsed,
    accessWindowStatus,
    setAccessWindowStatus,
    aiPanelOpen,
    fetchSatellites,
  } = useMDCockpit();
  const { initClockFromModel } = cesium;
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const orbitEventsSocketRef = useRef<WebSocket | null>(null);
  const orbitEventsReconnectRef = useRef<number | null>(null);
  const orbitFetchKeysRef = useRef<Set<string>>(new Set());
  const applyOrbitEventRef = useRef(applyOrbitEvent);
  const updateSatellitesRef = useRef(updateSatellites);
  const accessWindowRunRef = useRef<{
    runId?: string;
    resolve: () => void;
    reject: (error?: unknown) => void;
  } | null>(null);
  const accessWindowRunStateRef = useRef<AccessWindowRunState | null>(null);
  const accessAnalysisReportRef = useRef<{ reportId?: string; status: AccessAnalysisStatus } | null>(null);
  const coverageAnalysisReportRef = useRef<{ reportId?: string; status: string } | null>(null);
  const [orbitUpdateLock, setOrbitUpdateLock] = useState<OrbitUpdateLock | null>(null);
  const [accessWindowRun, setAccessWindowRun] = useState<AccessWindowRunState | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [splashFinished, setSplashFinished] = useState(false);
  const [isEditingThresholds, setIsEditingThresholds] = useState(false);
  const [tempThresholds, setTempThresholds] = useState<string[]>(['2', '4', '6', '8']);

  const setAccessRunState = (state: AccessWindowRunState | null) => {
    accessWindowRunStateRef.current = state;
    setAccessWindowRun(state);
  };

  const satelliteCalculation = useMemo(() => {
    const isComplete = (status: OrbitStatusMeta | undefined, fallbackTotal?: number) => {
      if (!status || status.status !== 'completed') return false;
      const total = status.satellitesTotal ?? fallbackTotal;
      if (total == null) return true;
      return (status.satellitesDone ?? total) >= total;
    };

    let total = 0;
    let completed = 0;
    let ready = satellites.length > 0;

    satellites.forEach((item) => {
      if ((item as SatGroup).type === 'group') {
        const group = item as SatGroup;
        const groupTotal = group.orbitPointStatus?.satellitesTotal ?? group.children.length;
        total += groupTotal;

        if (isComplete(group.orbitPointStatus, groupTotal)) {
          completed += groupTotal;
          return;
        }

        const completedChildren = group.children.filter(child => isComplete(child.orbitPointStatus) || child.propagationStatus === 'ready').length;
        completed += completedChildren;
        if (groupTotal === 0 || completedChildren < groupTotal) ready = false;
        return;
      }

      total += 1;
      const sat = item as Satellite;
      if (isComplete(sat.orbitPointStatus) || sat.propagationStatus === 'ready') completed += 1;
      else ready = false;
    });

    if (total === 0 || completed < total) ready = false;
    return { ready, completed, total };
  }, [satellites]);

  useEffect(() => {
    applyOrbitEventRef.current = applyOrbitEvent;
    updateSatellitesRef.current = updateSatellites;
  }, [applyOrbitEvent, updateSatellites]);

  const applyOrbitReports = (reports: any[], runId?: string, updatedAt?: string) => {
    const updates = reports.reduce((acc: Record<string, any>, report: any) => ({
      ...acc,
      ...mapOrbitReportToSatelliteUpdates(report, runId, updatedAt),
    }), {});
    if (Object.keys(updates).length > 0) {
      updateSatellitesRef.current(updates);
    }
  };

  const fetchGroupOrbit = async (groupId: string, runId?: string, updatedAt?: string) => {
    if (!mission_id || !scenario_id || !groupId) return;
    const fetchKey = `${groupId}:${runId ?? 'latest'}`;
    if (orbitFetchKeysRef.current.has(fetchKey)) return;
    orbitFetchKeysRef.current.add(fetchKey);
    try {
      const res = await MissionService.getSatelliteGroupOrbit(mission_id, scenario_id, groupId);
      const report = res?.data?.data ?? res?.data;
      applyOrbitReports(report ? [report] : [], runId, updatedAt);
    } catch (error) {
      orbitFetchKeysRef.current.delete(fetchKey);
      console.error('[OrbitPoints] Group orbit fetch failed', { groupId, error });
    }
  };

  const fetchAllCompletedOrbitReports = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      const res = await MissionService.getAllSatelliteGroupOrbit(mission_id, scenario_id);
      const reports = res?.data?.data ?? res?.data ?? [];
      if (Array.isArray(reports)) applyOrbitReports(reports);
    } catch (error) {
      console.error('[OrbitPoints] All group orbit fetch failed', error);
    }
  };

  const getAccessWindowLabel = (state: AccessWindowRunState | null) => {
    if (!state) return 'Updating access windows';
    const total = state.pairsTotal ?? 0;
    const done = state.pairsDone ?? 0;
    const failed = state.pairsFailed ?? 0;
    const progress = total > 0 ? `${done + failed}/${total}` : '';
    const failedText = failed > 0 ? ` · ${failed} failed` : '';
    if (state.status === 'completed') return `Access windows complete${progress ? ` ${progress}` : ''}${failedText}`;
    if (state.status === 'failed') return state.error || `Access windows failed${progress ? ` ${progress}` : ''}${failedText}`;
    return `Access windows ${progress || 'running'}${failedText}`;
  };

  const toAccessRunState = (run: any, fallbackStatus?: AccessWindowStatus): AccessWindowRunState | null => {
    if (!run) return null;
    const rawStatus = String(run.status ?? fallbackStatus ?? '').toLowerCase();
    const status: AccessWindowStatus = rawStatus === 'completed' || rawStatus === 'failed'
      ? rawStatus
      : rawStatus === 'progress'
        ? 'progress'
        : 'running';
    return {
      status,
      runId: run.runId,
      pairsTotal: run.pairsTotal,
      pairsDone: run.pairsDone,
      pairsFailed: run.pairsFailed,
      error: run.error,
    };
  };

  const fetchAccessWindowList = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      const res = await MissionService.getAccessWindows(mission_id, scenario_id, {
        pageSize: 1000,
      });
      const data = res?.data?.data ?? res?.data;
      setAccessWindowPairs(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('[AccessWindows] List fetch failed', error);
    }
  };

  const fetchAccessAnalysisReports = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      const res = await MissionService.getAccessAnalysisReports(mission_id, scenario_id);
      const data = res?.data?.data ?? res?.data;
      setAccessAnalysisReports(mapAccessAnalysisReportsResponse(data));
    } catch (error) {
      console.error('[AccessAnalysis] Reports fetch failed', error);
    }
  };

  const finishAccessWindowRun = (status: 'completed' | 'failed', error?: unknown) => {
    const pending = accessWindowRunRef.current;
    accessWindowRunRef.current = null;
    setOrbitUpdateLock(null);
    if (status === 'completed') {
      setAccessWindowStatus('ready');
      setShouldBlockUI(false);
      void fetchAccessWindowList();
      pending?.resolve();
    } else {
      setAccessWindowStatus('failed');
      setShouldBlockUI(false);
      pending?.reject(error ?? new Error('Access-window computation failed'));
    }
  };

  const applyAccessRunState = (state: AccessWindowRunState | null) => {
    setAccessRunState(state);
    if (!state) return;
    setShouldBlockUI(state.status === 'running' || state.status === 'progress');
    setAccessWindowStatus(state.status === 'running' || state.status === 'progress' ? 'computing' : state.status === 'completed' ? 'ready' : 'failed');
    if (state.status === 'completed') finishAccessWindowRun('completed');
    if (state.status === 'failed') finishAccessWindowRun('failed', new Error(state.error || 'Access-window computation failed'));
  };

  const initializeAccessWindowStatus = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      const res = await MissionService.getAccessWindowStatusOverview(mission_id, scenario_id);
      const overview = res?.data?.data ?? res?.data;
      if (!overview) return;
      const status = String(overview.accessWindowStatus ?? '').toLowerCase();
      const activeRun = overview.activeRun;
      const latestRun = overview.latestRun;

      if (status === 'computing' && activeRun?.runId) {
        const state = toAccessRunState(activeRun, 'running');
        if (state) {
          setAccessRunState(state);
          setShouldBlockUI(true);
          setAccessWindowStatus('computing');
        }
        return;
      }

      if (status === 'ready') {
        const state = toAccessRunState(latestRun ?? { status: 'completed' }, 'completed');
        setAccessRunState(state);
        setAccessWindowStatus('ready');
        setShouldBlockUI(false);
        void fetchAccessWindowList();
        return;
      }

      if (status === 'failed') {
        const state = toAccessRunState(latestRun ?? { status: 'failed' }, 'failed');
        setAccessRunState(state);
        setAccessWindowStatus('failed');
        setShouldBlockUI(false);
        return;
      }

      // If status is not recognized, default to not blocking
      setShouldBlockUI(false);
    } catch (error) {
      console.error('[AccessWindows] Status overview fetch failed', error);
    }
  };

  const applyAccessWindowEvent = (message: any) => {
    const status = String(message.status ?? '').toLowerCase();
    if (!['running', 'progress', 'completed', 'failed'].includes(status)) return;
    if (message.scenarioId && scenario_id && message.scenarioId !== scenario_id) return;

    const next: AccessWindowRunState = {
      status: status as AccessWindowStatus,
      runId: message.runId,
      pairsTotal: message.pairsTotal,
      pairsDone: message.pairsDone,
      pairsFailed: message.pairsFailed,
      error: message.error,
    };
    setAccessRunState(next);
    setOrbitUpdateLock({ label: getAccessWindowLabel(next) });

    const pending = accessWindowRunRef.current;
    if (!pending) {
      if (status === 'completed') finishAccessWindowRun('completed');
      if (status === 'failed') finishAccessWindowRun('failed', new Error(message.error || 'Access-window computation failed'));
      return;
    }
    if (pending.runId && message.runId && pending.runId !== message.runId) return;

    if (status === 'completed') {
      finishAccessWindowRun('completed');
    } else if (status === 'failed') {
      finishAccessWindowRun('failed', new Error(message.error || 'Access-window computation failed'));
    }
  };

  const applyAccessAnalysisEvent = (message: any) => {
    const rawStatus = message.status ?? message.analysisStatus ?? message.reportStatus ?? message.jobStatus ?? '';
    const status = String(rawStatus).toLowerCase() as AccessAnalysisStatus;
    if (!['analyzing', 'calculating_access_windows', 'in_progress', 'processing', 'queued', 'pending', 'completed', 'failed'].includes(status)) return;
    if (message.scenarioId && scenario_id && message.scenarioId !== scenario_id) return;
    if (message.missionId && mission_id && message.missionId !== mission_id) return;

    const rawReportId = message.reportId ?? message.accessAnalysisId ?? message.analysisId ?? message.id;
    const reportId = rawReportId ? String(rawReportId) : undefined;
    const previous = accessAnalysisReportRef.current;
    if (previous && previous.reportId === reportId && previous.status === status) return;

    accessAnalysisReportRef.current = { reportId, status };
    if (reportId) {
      upsertAccessAnalysisReport({
        reportId,
        name: message.reportName ?? message.name ?? message.report_name ?? `REPORT_${reportId.slice(0, 8)}`,
        status,
        updatedAt: message.updatedAt ?? Date.now(),
        raw: message,
      });
    }

    if (status === 'completed') {
      void fetchAccessAnalysisReports();
      void fetchAllCompletedOrbitReports();
      showToastMessage('success', message.message || 'Access analysis report is ready');
    } else if (status === 'failed') {
      showToastMessage('error', message.error || 'Access analysis failed');
    }
  };

  const fetchCoverageAnalysisReports = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      const res = await MissionService.getCoverageAnalysisReports(mission_id, scenario_id);
      const data = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
      setCoverageAnalysisReports(list.map((r: any, idx: number) => ({
        reportId: r.coverageAnalysisId ?? r.reportId ?? r.id ?? '',
        name: r.reportName ?? r.name ?? r.report_name ?? '',
        status: String(r.analysisStatus ?? r.status ?? 'completed').toLowerCase(),
        createdAt: r.createdAt ?? r.created_at ?? null,
        updatedAt: r.updatedAt ?? r.updated_at ?? null,
        raw: r,
      })).filter((r: any) => r.reportId));
    } catch (error) {
      console.error('[CoverageAnalysis] Reports fetch failed', error);
    }
  };

  const applyCoverageAnalysisEvent = (message: any) => {
    const status = String(message.analysisStatus ?? message.status ?? '').toLowerCase();
    if (!['analyzing', 'calculating_coverage', 'in_progress', 'completed', 'failed'].includes(status)) return;
    if (message.scenarioId && scenario_id && message.scenarioId !== scenario_id) return;
    if (message.missionId && mission_id && message.missionId !== mission_id) return;

    const reportId = (message.coverageAnalysisId ?? message.reportId) ? String(message.coverageAnalysisId ?? message.reportId) : undefined;
    const previous = coverageAnalysisReportRef.current;
    if (previous && previous.reportId === reportId && previous.status === status) return;

    coverageAnalysisReportRef.current = { reportId, status };
    if (reportId) {
      upsertCoverageAnalysisReport({
        reportId,
        name: message.reportName ?? message.name ?? message.report_name ?? '',
        status,
        updatedAt: message.updatedAt ?? Date.now(),
        raw: message,
      });
    }

    if (status === 'completed') {
      void fetchCoverageAnalysisReports();
      showToastMessage('success', message.message || 'Coverage analysis report is ready');
    } else if (status === 'failed') {
      showToastMessage('error', message.error || 'Coverage analysis failed');
    }
  };

  const handleMissionUpdate = async () => {
    if (orbitUpdateLock || !satelliteCalculation.ready || !mission_id || !scenario_id) return;
    const body: Record<string, unknown> = { retryFailed: false };
    const prop = modelData?.propagation;
    if (prop?.startEpoch != null) body.fromUtc = prop.startEpoch;
    if (prop?.endEpoch != null) body.toUtc = prop.endEpoch;

    const initialState: AccessWindowRunState = { status: 'running', pairsTotal: 0, pairsDone: 0, pairsFailed: 0 };
    setAccessRunState(initialState);
    setShouldBlockUI(true);
    setAccessWindowStatus('computing');

    try {
      await new Promise<void>((resolve, reject) => {
        accessWindowRunRef.current = { resolve, reject };
        MissionService.runAccessWindows(mission_id, scenario_id, body)
          .then((res: any) => {
            const data = res?.data?.data ?? res?.data;
            const runId = data?.runId;
            if (accessWindowRunRef.current) accessWindowRunRef.current.runId = runId;
            const runningState: AccessWindowRunState = { ...initialState, runId, status: data?.status ?? 'running' };
            applyAccessRunState(runningState);
          })
          .catch(reject);
      });
    } catch (error) {
      accessWindowRunRef.current = null;
      setAccessWindowStatus('failed');
      setShouldBlockUI(false);
      throw error;
    }
  };

  useEffect(() => {
    if (!shouldBlockUI) return;
    const blockCreateEditKeys = (event: KeyboardEvent) => {
      const createKeys = ['n', 'N'];
      const editKey = 'e';
      if (createKeys.includes(event.key) || (event.key === editKey && !event.ctrlKey && !event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockCreateEditKeys, true);
    return () => window.removeEventListener('keydown', blockCreateEditKeys, true);
  }, [shouldBlockUI]);

  useEffect(() => {
    if (!mission_id || !scenario_id) return;
    setAccessWindowPairs([]);
    setAccessAnalysisReports([]);
    setCoverageAnalysisReports([]);
    setAccessWindowStatus(null);
    setShouldBlockUI(false);
    fetchGroundStations(mission_id, scenario_id);
    fetchGeoFeatures(mission_id, scenario_id);
    void initializeAccessWindowStatus();
    void fetchAccessAnalysisReports();
    void fetchCoverageAnalysisReports();

    MissionService.getMissionModelById(mission_id, scenario_id)
      .then((res: any) => {
        const data = res?.data?.data ?? res?.data;
        if (data) {
          setModelData({ name: data.name, perturbationForces: data.perturbationForces, propagation: data.propagation });
          const prop = data.propagation;
          if (!data.perturbationForces || !isPropagationConfigured(prop)) {
            openModelConfig();
          }
          if (prop?.startEpoch && prop?.endEpoch) {
            initClockFromModel(prop.startEpoch, prop.endEpoch);
          }
        }
      })
      .catch(() => { /* silent — modelling still usable */ });

    (async () => {
      let showedInitialLoader = false;
      try {
        setOrbitUpdateLock({
          label: 'Preparing orbital view',
          mode: 'initialOrbitLoad',
        });
        showedInitialLoader = true;
        await fetchSatellites(mission_id, scenario_id);
      } catch (error) {
        console.error('[SatelliteGroups] Initial fetch failed', error);
      } finally {
        if (showedInitialLoader) {
          setOrbitUpdateLock(null);
        }
        setIsInitialLoading(false);
      }
    })();

    MissionService.getSatelliteGroupStatusOverview(mission_id, scenario_id)
      .then((res: any) => {
        const list: any[] = res?.data?.data ?? res?.data ?? [];
        if (!Array.isArray(list)) return;
        list.forEach((item: any) => {
          const groupId = item.groupId ?? item.group_id ?? item.id;
          const status = String(item.orbit_propagator_status ?? item.overall_status ?? item.status ?? item.orbitPointStatus ?? '').toLowerCase();
          if (groupId && ['pending', 'in_progress', 'completed', 'failed'].includes(status)) {
            applyOrbitEventRef.current({
              subType: 'group',
              status: status as OrbitPointStatus,
              groupId,
              runId: item.runId ?? item.orbit_run_id ?? item.orbitRunId,
              satellitesTotal: item.satellitesTotal,
              satellitesDone: item.satellitesDone,
              error: item.error,
              updatedAt: item.updatedAt,
            });
          }

          const satelliteStatuses = item.satellite_statuses ?? item.satelliteStatuses ?? [];
          if (Array.isArray(satelliteStatuses)) {
            satelliteStatuses.forEach((sat: any) => {
              const satelliteId = sat.satelliteId ?? sat.satellite_id ?? sat.id;
              const satStatus = String(sat.status ?? sat.orbitPointStatus ?? '').toLowerCase();
              if (satelliteId && ['pending', 'in_progress', 'completed', 'failed'].includes(satStatus)) {
                applyOrbitEventRef.current({
                  subType: 'satellite',
                  status: satStatus as OrbitPointStatus,
                  groupId,
                  satelliteId,
                  runId: sat.runId ?? sat.run_id ?? item.runId ?? item.orbit_run_id ?? item.orbitRunId,
                  error: sat.error,
                  updatedAt: sat.updatedAt ?? sat.completedAt ?? sat.startedAt,
                });
              }
            });
          }
        });
      })
      .catch(() => { /* status overview is additive; group/all is still source of truth */ });
    return () => {
      accessWindowRunRef.current = null;
    };
  }, [mission_id, scenario_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mission_id || !scenario_id) return;
    const hasOngoingAccess = accessAnalysisReports.some(r => {
      const st = String(r.status).toLowerCase();
      return st !== 'completed' && st !== 'failed';
    });
    const hasOngoingCoverage = coverageAnalysisReports.some(r => {
      const st = String(r.status).toLowerCase();
      return st !== 'completed' && st !== 'failed';
    });
    if (!hasOngoingAccess && !hasOngoingCoverage) return;

    const interval = window.setInterval(() => {
      if (hasOngoingAccess) void fetchAccessAnalysisReports();
      if (hasOngoingCoverage) void fetchCoverageAnalysisReports();
    }, 4000);

    return () => window.clearInterval(interval);
  }, [mission_id, scenario_id, accessAnalysisReports, coverageAnalysisReports]);

  const activeReport = selectedReportDetail || selectedCoverageReportDetail;

  useEffect(() => {
    if (activeReport?.raw?.requestInput) {
      const input = activeReport.raw.requestInput;
      const reportStart = input.startTime;
      const duration = input.duration ?? input.durationHrs ?? input.durationHours ?? 24;
      if (reportStart) {
        const reportEnd = reportStart + (duration * 3600);
        initClockFromModel(reportStart, reportEnd);
      }
    } else {
      const prop = modelData?.propagation;
      if (prop?.startEpoch && prop?.endEpoch) {
        initClockFromModel(prop.startEpoch, prop.endEpoch);
      }
    }
  }, [activeReport, modelData?.propagation, initClockFromModel]);

  useEffect(() => {
    if (!mission_id || !scenario_id) return;

    const baseUrl = getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL');
    if (!baseUrl) {
      console.warn('[MissionSimulationWS] Missing REACT_APP_WEBSOCKET_BACKEND_BASE_URL');
      return;
    }

    let disposed = false;
    let keepAliveId: number | null = null;

    const wsUrl = `${baseUrl.replace(/\/?$/, '/')}${[
      'mission-simulation',
      encodeURIComponent(mission_id),
      'scenario',
      encodeURIComponent(scenario_id),
      'events',
      'ws',
    ].join('/')}`;

    const clearReconnect = () => {
      if (orbitEventsReconnectRef.current == null) return;
      window.clearTimeout(orbitEventsReconnectRef.current);
      orbitEventsReconnectRef.current = null;
    };

    const clearKeepAlive = () => {
      if (keepAliveId == null) return;
      window.clearInterval(keepAliveId);
      keepAliveId = null;
    };

    const connect = () => {
      if (disposed) return;
      clearReconnect();

      const ws = new WebSocket(wsUrl);
      orbitEventsSocketRef.current = ws;

      clearKeepAlive();
      keepAliveId = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30_000);

      ws.onopen = () => { };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type === 'orbit_event') {
            const rawSubType = message.subType ?? message.sub_type;
            const subType = typeof rawSubType === 'string' ? rawSubType.toLowerCase() : '';
            const status = typeof message.status === 'string' ? message.status.toLowerCase() : '';
            const groupId = message.groupId ?? message.group_id;
            const satelliteId = message.satelliteId ?? message.satellite_id;
            const runId = message.runId ?? message.run_id;
            const satellitesTotal = message.satellitesTotal ?? message.satellites_total;
            const satellitesDone = message.satellitesDone ?? message.satellites_done;

            if (
              (subType === 'group' || subType === 'satellite') &&
              ['pending', 'in_progress', 'completed', 'failed'].includes(status)
            ) {
              applyOrbitEventRef.current({
                subType: subType as any,
                status: status as any,
                groupId,
                satelliteId,
                runId,
                satellitesTotal,
                satellitesDone,
                error: message.error,
                updatedAt: message.updatedAt,
              });
              if (subType === 'group' && status === 'completed' && groupId) {
                void fetchGroupOrbit(groupId, runId, message.updatedAt);
              }
            }
          }
          if (message?.type === 'access_window_event') {
            applyAccessWindowEvent(message);
          }
          if (message?.type === 'access_analysis_event') {
            applyAccessAnalysisEvent(message);
          }
          if (message?.type === 'coverage_analysis_event') {
            applyCoverageAnalysisEvent(message);
          }
        } catch { }
      };

      ws.onerror = (event) => {
        console.error('[MissionSimulationWS] Error:', event);
      };

      ws.onclose = (event) => {
        if (orbitEventsSocketRef.current === ws) orbitEventsSocketRef.current = null;
        clearKeepAlive();
        if (!disposed) {
          orbitEventsReconnectRef.current = window.setTimeout(connect, 1500);
        }
      };
    };

    try {
      connect();
    } catch (error) {
      console.error('[MissionSimulationWS] Failed to create socket:', error);
    }

    return () => {
      disposed = true;
      clearReconnect();
      clearKeepAlive();
      const ws = orbitEventsSocketRef.current;
      orbitEventsSocketRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, 'Mission modelling unmounted');
      }
    };
  }, [mission_id, scenario_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderGapLegend = () => {
    if (!selectedCoverageReportId || coverageSectionTab !== 'gap') return null;

    const durationHrs = selectedCoverageReportDetail?.raw?.requestInput?.duration ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHrs ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHours ??
      24;
    const totalDurationMin = durationHrs * 60;
    const stepMin = totalDurationMin / 5;

    const formatVal = (valMin: number) => {
      if (valMin >= 60) {
        const hrs = valMin / 60;
        return hrs % 1 === 0 ? `${hrs}h` : `${hrs.toFixed(1)}h`;
      }
      return `${Math.round(valMin)}m`;
    };

    const timelineVisibleHeight = timelineCollapsed ? 34 : (timelineHeight ?? 168);
    const bottomPos = timelineVisibleHeight + 40;

    return (
      <div className={styles.gapLegend} style={{ bottom: `${bottomPos}px`, left: leftPanelCollapsed ? '12px' : '236px' }}>
        <div className={styles.gapLegendHeader}>
          <h4 className={styles.gapLegendTitle}>Gap</h4>
          <div style={{ width: 100 }}>
            <FigmaCustomSelect
              value={heatmapMetricMode}
              options={[
                { value: 'max', label: 'Maximum' },
                { value: 'min', label: 'Minimum' },
                { value: 'avg', label: 'Average' },
              ]}
              onChange={(val) => setHeatmapMetricMode(val as any)}
            />
          </div>
        </div>
        <div className={styles.gapLegendList}>
          <div className={styles.gapLegendItem}>
            <div className={styles.gapLegendColorBox} style={{ background: '#4dd609' }} />
            <span className={styles.gapLegendLabel}>&lt; {formatVal(stepMin)}</span>
          </div>
          <div className={styles.gapLegendItem}>
            <div className={styles.gapLegendColorBox} style={{ background: '#93ed1d' }} />
            <span className={styles.gapLegendLabel}>{formatVal(stepMin)} - {formatVal(2 * stepMin)}</span>
          </div>
          <div className={styles.gapLegendItem}>
            <div className={styles.gapLegendColorBox} style={{ background: '#f2d03b' }} />
            <span className={styles.gapLegendLabel}>{formatVal(2 * stepMin)} - {formatVal(3 * stepMin)}</span>
          </div>
          <div className={styles.gapLegendItem}>
            <div className={styles.gapLegendColorBox} style={{ background: '#bf770a' }} />
            <span className={styles.gapLegendLabel}>{formatVal(3 * stepMin)} - {formatVal(4 * stepMin)}</span>
          </div>
          <div className={styles.gapLegendItem}>
            <div className={styles.gapLegendColorBox} style={{ background: '#ff3333' }} />
            <span className={styles.gapLegendLabel}>&gt; {formatVal(4 * stepMin)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Synchronize metric mode when tab switches
  useEffect(() => {
    if (coverageSectionTab === 'revisit') {
      if (heatmapMetricMode === 'duration') {
        setHeatmapMetricMode('count');
      }
    } else if (coverageSectionTab === 'gap') {
      if (heatmapMetricMode === 'count' || heatmapMetricMode === 'duration') {
        setHeatmapMetricMode('max');
      }
    }
  }, [coverageSectionTab, heatmapMetricMode, setHeatmapMetricMode]);

  useEffect(() => {
    if (revisitThresholds) {
      setTempThresholds(revisitThresholds.map(String));
    }
  }, [revisitThresholds]);

  const renderRevisitLegend = () => {
    if (!selectedCoverageReportId || coverageSectionTab !== 'revisit') return null;

    const durationHrs = selectedCoverageReportDetail?.raw?.requestInput?.duration ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHrs ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHours ??
      24;
    const totalDurationMin = durationHrs * 60;
    const stepMin = totalDurationMin / 5;

    const formatVal = (valMin: number) => {
      if (valMin >= 60) {
        const hrs = valMin / 60;
        return hrs % 1 === 0 ? `${hrs}h` : `${hrs.toFixed(1)}h`;
      }
      return `${Math.round(valMin)}m`;
    };

    const timelineVisibleHeight = timelineCollapsed ? 34 : (timelineHeight ?? 168);
    const bottomPos = timelineVisibleHeight + 40;

    const t1 = revisitThresholds?.[0] ?? 2;
    const t2 = revisitThresholds?.[1] ?? 4;
    const t3 = revisitThresholds?.[2] ?? 6;
    const t4 = revisitThresholds?.[3] ?? 8;

    const handleSave = () => {
      const parsed = tempThresholds.map(val => {
        const num = parseInt(val, 10);
        return isNaN(num) ? 0 : num;
      });
      setRevisitThresholds(parsed);
      setIsEditingThresholds(false);
    };

    const handleCancel = () => {
      if (revisitThresholds) {
        setTempThresholds(revisitThresholds.map(String));
      }
      setIsEditingThresholds(false);
    };

    // Derive top-level mode: 'count' or 'duration'
    const isDurationMode = heatmapMetricMode !== 'count';
    const topMode = isDurationMode ? 'duration' : 'count';
    // Sub-mode for duration: max, min, avg
    const subMode = isDurationMode ? heatmapMetricMode : 'max';

    // Parse thresholds for validation
    const parsedThresholds = tempThresholds.map(v => {
      const n = parseInt(v, 10);
      return isNaN(n) ? null : n;
    });
    const [p0, p1, p2, p3] = parsedThresholds;

    // Validate: non-null, non-negative, strictly ascending
    const isValid0 = p0 !== null && p0 >= 0;
    const isValid1 = p1 !== null && p1 >= 0 && (p0 !== null ? p1 > p0 : true);
    const isValid2 = p2 !== null && p2 >= 0 && (p1 !== null ? p2 > p1 : true);
    const isValid3 = p3 !== null && p3 >= 0 && (p2 !== null ? p3 > p2 : true);
    const allValid = isValid0 && isValid1 && isValid2 && isValid3;

    const invalidCls = styles.gapLegendInputInvalid;

    return (
      <div style={{ position: 'absolute', left: leftPanelCollapsed ? 12 : 236, bottom: `${bottomPos}px`, display: 'flex', alignItems: 'flex-start', gap: 8, zIndex: 10, transition: 'bottom 0.25s ease-in-out, left 0.25s ease-in-out' }}>
        {/* Main card */}
        <div className={styles.gapLegend} style={{ position: 'relative', bottom: 'unset', left: 'unset' }}>
          {/* Header: Title + Count/Duration dropdown */}
          <div className={styles.gapLegendHeader}>
            <h4 className={styles.gapLegendTitle}>Revisit</h4>
            <div style={{ width: 100 }}>
              <FigmaCustomSelect
                value={topMode}
                options={[
                  { value: 'count', label: 'Count' },
                  { value: 'duration', label: 'Duration' },
                ]}
                onChange={(val) => {
                  if (val === 'count') {
                    setHeatmapMetricMode('count');
                  } else {
                    setHeatmapMetricMode('max');
                  }
                }}
              />
            </div>
          </div>

          {/* Sub-dropdown: Maximum/Minimum/Average (only when Duration) */}
          {isDurationMode && (
            <div style={{ width: 100 }}>
              <FigmaCustomSelect
                value={subMode}
                options={[
                  { value: 'max', label: 'Maximum' },
                  { value: 'min', label: 'Minimum' },
                  { value: 'avg', label: 'Average' },
                ]}
                onChange={(val) => setHeatmapMetricMode(val as any)}
              />
            </div>
          )}

          {/* Color legend items */}
          <div className={styles.gapLegendList}>
            {/* Row 1 */}
            <div className={styles.gapLegendItem}>
              <div className={styles.gapLegendColorBox} style={{ background: isDurationMode ? '#4dd609' : '#ff3333' }} />
              {isDurationMode ? (
                <span className={styles.gapLegendLabel}>&lt; {formatVal(stepMin)}</span>
              ) : isEditingThresholds ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className={styles.gapLegendLabel}>&lt;</span>
                  <input
                    type="number"
                    min="0"
                    className={`${styles.gapLegendInput} ${!isValid0 ? invalidCls : ''}`}
                    value={tempThresholds[0]}
                    onChange={(e) => setTempThresholds([e.target.value, tempThresholds[1], tempThresholds[2], tempThresholds[3]])}
                  />
                </div>
              ) : (
                <span className={styles.gapLegendLabel}>&lt;{t1}</span>
              )}
            </div>

            {/* Row 2 */}
            <div className={styles.gapLegendItem}>
              <div className={styles.gapLegendColorBox} style={{ background: isDurationMode ? '#93ed1d' : '#bf770a' }} />
              {isDurationMode ? (
                <span className={styles.gapLegendLabel}>{formatVal(stepMin)} - {formatVal(2 * stepMin)}</span>
              ) : isEditingThresholds ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" className={styles.gapLegendInput} value={tempThresholds[0]} disabled />
                  <span className={styles.gapLegendLabel}>-</span>
                  <input
                    type="number"
                    min="0"
                    className={`${styles.gapLegendInput} ${!isValid1 ? invalidCls : ''}`}
                    value={tempThresholds[1]}
                    onChange={(e) => setTempThresholds([tempThresholds[0], e.target.value, tempThresholds[2], tempThresholds[3]])}
                  />
                </div>
              ) : (
                <span className={styles.gapLegendLabel}>{t1}-{t2}</span>
              )}
            </div>

            {/* Row 3 */}
            <div className={styles.gapLegendItem}>
              <div className={styles.gapLegendColorBox} style={{ background: '#f2d03b' }} />
              {isDurationMode ? (
                <span className={styles.gapLegendLabel}>{formatVal(2 * stepMin)} - {formatVal(3 * stepMin)}</span>
              ) : isEditingThresholds ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" className={styles.gapLegendInput} value={tempThresholds[1]} disabled />
                  <span className={styles.gapLegendLabel}>-</span>
                  <input
                    type="number"
                    min="0"
                    className={`${styles.gapLegendInput} ${!isValid2 ? invalidCls : ''}`}
                    value={tempThresholds[2]}
                    onChange={(e) => setTempThresholds([tempThresholds[0], tempThresholds[1], e.target.value, tempThresholds[3]])}
                  />
                </div>
              ) : (
                <span className={styles.gapLegendLabel}>{t2}-{t3}</span>
              )}
            </div>

            {/* Row 4 */}
            <div className={styles.gapLegendItem}>
              <div className={styles.gapLegendColorBox} style={{ background: isDurationMode ? '#bf770a' : '#93ed1d' }} />
              {isDurationMode ? (
                <span className={styles.gapLegendLabel}>{formatVal(3 * stepMin)} - {formatVal(4 * stepMin)}</span>
              ) : isEditingThresholds ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" className={styles.gapLegendInput} value={tempThresholds[2]} disabled />
                  <span className={styles.gapLegendLabel}>-</span>
                  <input
                    type="number"
                    min="0"
                    className={`${styles.gapLegendInput} ${!isValid3 ? invalidCls : ''}`}
                    value={tempThresholds[3]}
                    onChange={(e) => setTempThresholds([tempThresholds[0], tempThresholds[1], tempThresholds[2], e.target.value])}
                  />
                </div>
              ) : (
                <span className={styles.gapLegendLabel}>{t3}-{t4}</span>
              )}
            </div>

            {/* Row 5 */}
            <div className={styles.gapLegendItem}>
              <div className={styles.gapLegendColorBox} style={{ background: isDurationMode ? '#ff3333' : '#4dd609' }} />
              {isDurationMode ? (
                <span className={styles.gapLegendLabel}>&gt; {formatVal(4 * stepMin)}</span>
              ) : isEditingThresholds ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className={styles.gapLegendLabel}>&gt;</span>
                  <input type="number" className={styles.gapLegendInput} value={tempThresholds[3]} disabled />
                </div>
              ) : (
                <span className={styles.gapLegendLabel}>&gt;{t4}</span>
              )}
            </div>
          </div>

          {/* Cancel / Save footer */}
          {isEditingThresholds && (
            <div className={styles.gapLegendFooter}>
              <button type="button" className={styles.gapLegendBtn} onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className={styles.gapLegendBtn} onClick={handleSave} disabled={!allValid}>
                Save
              </button>
            </div>
          )}
        </div>

        {/* Edit pencil icon OUTSIDE the card (Only for Count mode) */}
        {!isEditingThresholds && !isDurationMode && (
          <button
            type="button"
            onClick={() => setIsEditingThresholds(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 6,
              color: '#eee',
              cursor: 'pointer',
              padding: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 6,
              width: 28,
              height: 28,
              transition: 'all 0.15s ease',
            }}
            title="Edit thresholds"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.00223 13.5996C9.22303 13.5998 9.40165 13.7792 9.40165 14C9.40165 14.2208 9.22303 14.4002 9.00223 14.4004H3.16825C2.94751 14.4002 2.76884 14.2208 2.76884 14C2.76884 13.7792 2.94751 13.5998 3.16825 13.5996H9.00223ZM11.9329 4.41406C11.9329 4.14523 11.8261 3.88741 11.636 3.69727C11.4458 3.50706 11.1872 3.40039 10.9183 3.40039C10.6494 3.40049 10.3916 3.50716 10.2015 3.69727L4.19266 9.70605V9.70703C4.08192 9.81763 4.00043 9.95432 3.95634 10.1045L3.43095 11.9014L5.2288 11.376C5.37915 11.3321 5.51643 11.2513 5.62723 11.1406L11.636 5.13184C11.8262 4.94163 11.9329 4.68305 11.9329 4.41406ZM12.7327 4.41406C12.7327 4.89522 12.5417 5.35703 12.2015 5.69727L6.19266 11.7061C5.98687 11.9117 5.73265 12.063 5.45341 12.1445L3.53837 12.7041C3.4122 12.7409 3.27799 12.7435 3.15067 12.7109C3.02332 12.6783 2.90672 12.6115 2.81376 12.5186C2.72104 12.4257 2.65498 12.3097 2.62235 12.1826C2.58973 12.0553 2.59238 11.9211 2.62919 11.7949L3.18876 9.87891L3.26298 9.6748C3.34957 9.47606 3.47292 9.29471 3.62723 9.14062L9.63602 3.13184C9.97616 2.7917 10.4372 2.59971 10.9183 2.59961C11.3994 2.59961 11.8612 2.7916 12.2015 3.13184C12.5415 3.47201 12.7327 3.93306 12.7327 4.41406Z" fill="#EEEEEE" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.cockpit} md-ai-cockpit-page`}>
      {!splashFinished && (
        <LoadingScreen
          active={isInitialLoading}
          onFinished={() => setSplashFinished(true)}
        />
      )}

      {!isInitialLoading && (
        <>
          {renderGapLegend()}
          {renderRevisitLegend()}
          {/* z-index 0 — 3D globe */}
          <CesiumMap cesium={cesium} />

          {/* z-index 3 — edge vignette */}
          <MapVignette />

          {/* z-index 10 — top chrome */}
          <TopBar />

          {/* z-index 8 — bottom chrome */}
          <Timeline
            updateLocked={!!orbitUpdateLock}
            updateReady={satelliteCalculation.ready}
            updateDisabledReason={
              satelliteCalculation.total > 0
                ? `Waiting for satellite calculations (${satelliteCalculation.completed}/${satelliteCalculation.total})`
                : 'Waiting for satellites'
            }
            onMissionUpdate={handleMissionUpdate}
            accessWindowStatus={accessWindowStatus}
            accessWindowRunState={accessWindowRun}
          />
          <StatusBar accessWindowStatus={accessWindowStatus} accessWindowRunState={accessWindowRun} />

          {/* z-index 7 — command bar */}
          <CommandBar />

          {/* z-index 6 — side panels + map tools */}
          <LeftSideBar />
          <PropertyPanel />
          <RightTools />

          {/* z-index 30 — floating panels */}
          <GeoFeaturePanel />
          <AnalyserPanel />
          <CoverageAnalysisPanel />
          <AccessAnalysisReportsPanel />
          {aiPanelOpen && <AIPanel />}

          {/* z-index 20+ — modals */}
          <AddSatModal />
          <AddGroundStationModal />
          <ModelConfigModal />
        </>
      )}
    </div>
  );
};

export default MDCockpitInner;
